import { useQuery } from "@apollo/client";
import Button from "components/base/Button";
import Errors from "components/base/Errors";
import Spinner from "components/base/Spinner";
import PageV2 from "components/shared/PageV2";
import { Fragment } from "react";
import { useParams } from "react-router-dom";
import XLSX from "xlsx";
import { SCORE_COLORS } from "../const";
import { FETCH_ASSESSMENT_CATEGORY_SUMMARY } from "./graphql";
import { buildAssessmentCategorySummaryData } from "./utils";

const AssessmentCategoryPage = () => {
  const { id } = useParams();
  const { loading, error, data } = useQuery(FETCH_ASSESSMENT_CATEGORY_SUMMARY, {
    variables: { assessmentCategoryId: id },
  });

  if (loading) return <Spinner />;
  if (error) return <Errors error={error} />;

  let res = {};
  try {
    res = JSON.parse(data.esgAssessmentReport || "{}");
  } catch (e) {
    return <Errors error={e} />;
  }

  const { suppliers, sections, overallAvgRow } =
    buildAssessmentCategorySummaryData(res);

  const downloadXlsx = () => {
    const gradeRows = [];
    const valueRows = [];

    sections.forEach((section, sectionIndex) => {
      const sectionTitle = `${sectionIndex + 1}. ${section.title}`;
      const sectionGradeRow = { Number: sectionTitle };
      const sectionValueRow = { Number: sectionTitle };
      suppliers.forEach((supplier) => {
        sectionGradeRow[supplier.name] = "";
        sectionValueRow[supplier.name] = "";
      });
      gradeRows.push(sectionGradeRow);
      valueRows.push(sectionValueRow);

      section.questions.forEach((question) => {
        const gradeRow = { Number: question.indexNumber };
        const valueRow = { Number: question.indexNumber };
        suppliers.forEach((supplier, supplierIndex) => {
          const cell = question.cells[supplierIndex];
          gradeRow[supplier.name] = cell.gradeForExport;
          valueRow[supplier.name] = cell.valueForExport;
        });
        gradeRows.push(gradeRow);
        valueRows.push(valueRow);
      });

      const avgGradeRow = { Number: section.avgRow.label };
      const avgValueRow = { Number: section.avgRow.label };
      suppliers.forEach((supplier, supplierIndex) => {
        const cell = section.avgRow.cells[supplierIndex];
        avgGradeRow[supplier.name] = cell.gradeForExport;
        avgValueRow[supplier.name] = cell.valueForExport;
      });
      gradeRows.push(avgGradeRow);
      valueRows.push(avgValueRow);
    });

    const overallGradeRow = { Number: overallAvgRow.label };
    const overallValueRow = { Number: overallAvgRow.label };
    suppliers.forEach((supplier, supplierIndex) => {
      const cell = overallAvgRow.cells[supplierIndex];
      overallGradeRow[supplier.name] = cell.gradeForExport;
      overallValueRow[supplier.name] = cell.valueForExport;
    });
    gradeRows.push(overallGradeRow);
    valueRows.push(overallValueRow);

    const gradeSheet = XLSX.utils.json_to_sheet(gradeRows);
    const valueSheet = XLSX.utils.json_to_sheet(valueRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, gradeSheet, "ABCD");
    XLSX.utils.book_append_sheet(wb, valueSheet, "Score");
    const categoryName = data.esgAssessmentCategory?.name || "assessment";
    const fileName = `assessment_summary_${categoryName.replace(/[^a-z0-9_-]+/gi, "_")}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  return (
    <PageV2
      title={`Summary / ${data.esgAssessmentCategory.name}`}
      backTo="./.."
      toolbar={
        <div>
          <Button onClick={downloadXlsx}>Download XLSX</Button>
        </div>
      }
    >
      <div className="card p-0 relative overflow-auto">
        <table className="with-line whitespace-nowrap">
          <thead>
            <tr className="sticky top-0 z-20 backdrop-blur-lg bg-gray-50/90 dark:bg-gray-800/90">
              <th className="px-4 py-3 text-left sticky left-0 z-30 backdrop-blur-lg bg-gray-50/90 dark:bg-gray-800/90">
                Number
              </th>
              {suppliers.map((supplier) => (
                <th
                  key={supplier.id}
                  className="px-3 py-3 text-center min-w-28 border"
                >
                  {supplier.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sections.map((section, sectionIndex) => (
              <Fragment key={section.key}>
                <tr className="bg-gray-200 dark:bg-gray-700/50 sticky top-[41px] z-20 backdrop-blur-lg">
                  <td
                    className="px-4 py-2 font-semibold sticky left-0 z-10 bg-gray-200 dark:bg-gray-700/50"
                    colSpan={suppliers.length + 1}
                  >
                    {sectionIndex + 1}. {section.title}
                  </td>
                </tr>

                {section.questions.map((question) => (
                  <tr key={question.questionId}>
                    <td className="px-4 py-2 sticky left-0 z-10 bg-white dark:bg-gray-800">
                      {question.indexNumber}
                    </td>
                    {suppliers.map((supplier, supplierIndex) => {
                      const cell = question.cells[supplierIndex];
                      const cellClassName = `px-3 py-2 text-center border ${
                        cell.missingRequiredScore
                          ? "bg-red-200 dark:bg-red-800/50"
                          : ""
                      }`;
                      if (cell.displayScore === "-") {
                        return (
                          <td key={supplier.id} className={cellClassName}>
                            -
                          </td>
                        );
                      }
                      return (
                        <td key={supplier.id} className={cellClassName}>
                          <span
                            className={`font-semibold ${SCORE_COLORS[cell.scoreColorKey] || ""}`}
                          >
                            {cell.displayScore}
                          </span>
                        </td>
                      );
                    })}
                  </tr>
                ))}

                <tr className="bg-sky-50 dark:bg-sky-900/30">
                  <td className="px-4 py-2 font-semibold sticky left-0 z-10 bg-sky-50 dark:bg-sky-900/30">
                    {section.avgRow.label}
                  </td>
                  {suppliers.map((supplier, supplierIndex) => {
                    const cell = section.avgRow.cells[supplierIndex];
                    return (
                      <td
                        key={supplier.id}
                        className="px-3 py-2 text-center border font-semibold"
                      >
                        {cell.displayScore}
                      </td>
                    );
                  })}
                </tr>
              </Fragment>
            ))}
            <tr className="bg-emerald-50 dark:bg-emerald-900/30">
              <td className="px-4 py-2 font-semibold sticky left-0 z-10 bg-emerald-50 dark:bg-emerald-900/30">
                {overallAvgRow.label}
              </td>
              {suppliers.map((supplier, supplierIndex) => {
                const cell = overallAvgRow.cells[supplierIndex];
                return (
                  <td
                    key={supplier.id}
                    className="px-3 py-2 text-center border font-semibold"
                  >
                    {cell.displayScore}
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>

      <div className="opacity-70 text-xs">A = 10, B = 8, C = 5, D = 0.</div>
    </PageV2>
  );
};
export default AssessmentCategoryPage;
